import {useGetDatasetsInfo} from "api/api-hooks/datasets-hooks";
import {DatasetInfo} from "api/model/dataset/DatasetInfo";
import SimpleJsonExportButton from "components/basic/simple-json-export-button/SimpleJsonExportButton";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {MultiSelect} from "primereact/multiselect";
import {Tag} from "primereact/tag";
import React, {useMemo, useState} from "react";
import CopyTextWithButton from "../copy-text-with-button/CopyTextWithButton";

type DatasetsPropertiesTableProps = {
    identifiers: string[]
}

type DatasetInfoWithID = DatasetInfo & {
    id: string[]
}

type FormattedDataForExportProps = {
    dataset: string | null,
    name: string | null
}

type BodyMapperProps = {
    id: (rowData: DatasetInfoWithID) => React.ReactNode;
    title: (rowData: DatasetInfoWithID) => React.ReactNode;
    groupId: (rowData: DatasetInfoWithID) => React.ReactNode;
    categoryId: (rowData: DatasetInfoWithID) => React.ReactNode;
    datasetClass: (rowData: DatasetInfoWithID) => React.ReactNode;
    tags: (rowData: DatasetInfoWithID) => React.ReactNode;
};

const COLUMN_OPTIONS = [
    {label: "ID", value: "id"},
    {label: "Title", value: "title"},
    {label: "Group", value: "groupId"},
    {label: "Data type", value: "dataType"},
    {label: "Unit", value: "unit"},
    {label: "Category", value: "categoryId"},
    {label: "Dataset class", value: "datasetClass"},
    {label: "Access type", value: "accessType"},
    {label: "Decimal places", value: "decimalPlaces"},
    {label: "Tags", value: "tags"}
];

const DatasetsPropertiesTable = ({identifiers}: DatasetsPropertiesTableProps) => {
    const {data, isLoading, error} = useGetDatasetsInfo(identifiers);


    const [selectedColumns, setSelectedColumns] = useState<string[]>(["title", "id", "groupId", "dataType", "accessType"]);
    const [formattedDataForExport, setFormattedDataForExport] = useState<Array<FormattedDataForExportProps>>([]);

    const formattedData = useMemo(() => (
        data ? data.map(e => ({...e, id: e.identifier ? e.identifier.split(":") : []})) : []
    ), [data]);

    useMemo(() => {
        if (data) {
            setFormattedDataForExport(data.map(e => ({dataset: e.identifier, name: e.title})));
        }
    }, [data]);

    const idBody = (rowData: DatasetInfoWithID) => {
        return <CopyTextWithButton
            text={rowData.id[0]}
            copyableText={rowData.identifier!}
        />;
    };

    const titleBody = (rowData: DatasetInfoWithID) => {
        return <CopyTextWithButton
            text={rowData.title!}
            copyableText={rowData.title!}
        />;
    };

    const groupBody = (rowData: DatasetInfo) => {
        if (rowData.groupTitle && rowData.groupId) {
            return `${rowData.groupTitle} [${rowData.groupId}]`;
        } else if (rowData.groupTitle) {
            return rowData.groupTitle;
        } else if (rowData.groupId) {
            return `[${rowData.groupId}]`;
        } else {
            return "";
        }
    };

    const categoryBody = (rowData: DatasetInfo) => {
        if (rowData.categoryTitle && rowData.categoryId) {
            return `${rowData.categoryTitle} [${rowData.categoryId}]`;
        } else if (rowData.categoryTitle) {
            return rowData.categoryTitle;
        } else if (rowData.categoryId) {
            return `[${rowData.categoryId}]`;
        } else {
            return "";
        }
    };

    const datasetClassBody = (rowData: DatasetInfo) => {
        return <Tag value={rowData.datasetClass}></Tag>;
    };

    const tagsBody = (rowData: DatasetInfo) => {
        return <div className={"flex gap-1 flex-wrap"}>
            {rowData.tags.map((e, index) => <Tag value={e} key={index}></Tag>)}
        </div>;
    };

    if (error) {
        return <div>Failed to fetch dataset information: {error.message}</div>;
    }

    const bodyMapper: BodyMapperProps = {
        id: idBody,
        title: titleBody,
        groupId: groupBody,
        categoryId: categoryBody,
        datasetClass: datasetClassBody,
        tags: tagsBody,
    };

    return <div className="flex flex-column gap-1 relative">
        <div className="flex gap-3 mt-1 justify-content-end">
            <SimpleJsonExportButton data={formattedDataForExport} filename={"dataset-properties.json"}/>
            <MultiSelect value={selectedColumns} options={COLUMN_OPTIONS} onChange={(e) => setSelectedColumns(e.value)}
                         maxSelectedLabels={2}
            />
        </div>
        <DataTable value={formattedData} loading={isLoading} size={"small"}>
            {selectedColumns.map((column) => {
                const header = COLUMN_OPTIONS.find(e => e.value === column)?.label;
                return <Column key={column} header={header} field={column}
                               body={bodyMapper[column as keyof BodyMapperProps]}
                />;
            })}
        </DataTable>
    </div>;
};

export default DatasetsPropertiesTable;
